import { createRef, useCallback, useEffect, useMemo, useRef } from 'react';
import { getFocusableElements } from '../lib/get-focusable-elements.js';
import { getElementRect } from '../lib/get-element-rect.js';
import scopeLogger from '../lib/scope-logger.js';
import { useWindowEventListener } from './use-window-event-listener.js';

const eventName = 'mgg-spatial-navigation';

export { useSpatialNavigation, useSpatialEvent };

const console = scopeLogger('RMI:spatial-navigation');

/** @typedef {LEFT|UP|RIGHT|DOWN} SpatialDirection */

/**
 * @const
 * @type {number}
 */
const LEFT = 37;
/**
 * @name UP
 * @const {number}
 */
const UP = 38;
/**
 * @name RIGHT
 * @const {number}
 */
const RIGHT = 39;
/**
 * @const
 * @type {number}
 */
const DOWN = 40;

// TODO: should we use codes passed from app? I have doubt that codes for such keys may have variants.
const ARROW_KEY_CODE = {
  37: LEFT,
  38: UP,
  39: RIGHT,
  40: DOWN,
};

/**
 *
 * @type {{[key: SpatialDirection]: SpatialDirection}}
 */
export const DIRECTIONS = {
  LEFT,
  UP,
  RIGHT,
  DOWN,
};

/**
 * Create listener for navigation's keys press on document,
 * for every of them calculate the nearest candidate to move focus on.
 * Then emit spatial navigation event on same element,
 * having candidate reference and direction of navigation.
 *
 * Saves cursor click coordinates as base point to calculate candidates
 * on key up event occurs on non-focusable element.
 *
 * @emits mgg-spatial-navigation
 *
 * @example
 * new CustomEvent('mgg-spatial-navigation', {
 *   detail: {
 *     candidate,
 *     direction
 *   }
 * })
 */
function useSpatialNavigation() {
  /**
   * @type {React.MutableRefObject<Partial<DOMRectReadOnly>>}
   */
  const calculationBase = useRef({
    top: window.innerHeight * 0.5,
    left: window.innerWidth * 0.5,
    width: 0,
    height: 0,
  });

  const onMouseUp = useCallback(
    /** @param {MouseEvent} event */
    (event) => {
      calculationBase.current = {
        top: event.y,
        left: event.x,
        width: 0,
        height: 0,
      };
      console.log(
        'save click point %d, %d as base for future navigation',
        event.x,
        event.y,
      );
    },
    [],
  );

  useWindowEventListener('mouseup', onMouseUp, { passive: true });

  useEffect(() => {
    const container = document.documentElement;
    /**
     * @param {KeyboardEvent} event
     */
    const listener = (event) => {
      let direction = ARROW_KEY_CODE[event.keyCode];
      if (direction === undefined) {
        return;
      }
      /**
       *
       * @type {HTMLElement}
       */
      const startElement = event.target;

      /* TODO: ignore events from inputs elements */

      console.log(`handle ${event.key} for navigation from`, startElement);

      performance.mark('spatial:start');
      determineNavigationTarget(
        startElement,
        direction,
        calculationBase.current,
      )
        .then((candidate) => {
          const spatialEvent = new CustomEvent(eventName, {
            bubbles: true,
            detail: {
              candidate,
              origin: event,
              direction,
            },
          });
          startElement.dispatchEvent(spatialEvent);

          performance.mark('spatial:end');
          performance.measure('spatial', 'spatial:start');

          const [measureEntry] = performance.getEntriesByName('spatial');
          console.log(
            `Spatial navigation took ${measureEntry.duration} ms, navigation candidate: `,
            candidate,
          );
        })
        .catch((error) => {
          console.error('Spatial navigation error', error);
        });
    };

    container.addEventListener('keydown', listener);

    return () => {
      container.removeEventListener('keydown', listener);
    };
  }, []);
}

/**
 * @param {SpatialDirection} direction
 * @param {DOMRectReadOnly} base
 * @param {DOMRectReadOnly} target
 * @return {boolean}
 */
function directionFilters(direction, base, target) {
  switch (direction) {
    case LEFT: {
      return base.left + base.width * 0.5 >= target.left + target.width * 0.5;
    }
    case UP: {
      return base.top + base.height * 0.5 >= target.top + target.height * 0.5;
    }
    case RIGHT: {
      return target.left + target.width * 0.5 >= base.left + base.width * 0.5;
    }
    case DOWN: {
      return target.top + target.height * 0.5 >= base.top + base.height * 0.5;
    }
    default: {
      console.warn('unexpected direction', direction);
    }
  }
}

/**
 *
 * @param {SpatialDirection} direction
 * @param {DOMRectReadOnly} base
 * @param {DOMRectReadOnly} target
 * @return {[distance: number, deviation: number]}
 */
function calcDistanceRate(direction, base, target) {
  const baseCenter = {
    x: base.left + base.width * 0.5,
    y: base.top + base.height * 0.5,
  };
  const targetCenter = {
    x: target.left + target.width * 0.5,
    y: target.top + target.height * 0.5,
  };
  const distance = Math.hypot(
    baseCenter.x - targetCenter.x,
    baseCenter.y - targetCenter.y,
  );
  const directionDistance = [LEFT, RIGHT].includes(direction)
    ? Math.abs(baseCenter.x - targetCenter.x)
    : Math.abs(baseCenter.y - targetCenter.y);

  const deviation = 1 - directionDistance / distance;

  return [distance, deviation];
}

/**
 * @typedef {[candidate: HTMLElement, distance: number, deviation: number]} Candidate
 */

/**
 * compare candidates distance with penalty on deviation.
 *
 * @param {Candidate} candidateA
 * @param {Candidate} candidateB
 * @return {number}
 */
function byDistanceWithDeviationPenalty(candidateA, candidateB) {
  const [, aDist, aDevRate] = candidateA;
  const [, bDist, bDevRate] = candidateB;
  const rateA = rate(aDist, aDevRate);
  const rateB = rate(bDist, bDevRate);

  return rateA - rateB;
}

function rate(dist, deviation) {
  return dist * (1 + deviation);
}

function useSpatialEvent(ref, listener) {
  const staticRef = useMemo(() => {
    if (ref === undefined) {
      return createRef();
    }
    return ref;
  }, [ref]);

  useEffect(() => {
    const target = ref.current;

    target.addEventListener(eventName, listener);

    return () => {
      target.removeEventListener(eventName, listener);
    };
  }, [ref, listener]);
  return staticRef;
}

function determineNavigationTarget(
  startElement,
  direction,
  startPositionFallback,
) {
  const isListElement =
    startElement?.parentElement?.dataset?.role === 'scroll-container';
  if (isListElement) {
    let candidate = null;
    const scrollDirection =
      startElement?.parentElement?.dataset?.scrollDirection;
    if (scrollDirection === 'horizontal') {
      if (direction === LEFT) {
        candidate = startElement.previousElementSibling;
      } else if (direction === RIGHT) {
        candidate = startElement.nextElementSibling;
      }
    } else if (scrollDirection === 'vertical') {
      if (direction === UP) {
        candidate = startElement.previousElementSibling;
      } else if (direction === DOWN) {
        candidate = startElement.nextElementSibling;
      }
    }
    if (candidate !== null) {
      console.log('handle navigation in list, omit spatial navigation');
      return Promise.resolve(candidate);
    }
  }

  /* So far we have only up to screen size "slides" - it's ok to query the whole document */
  const focusableElements = getFocusableElements(document.documentElement);
  console.log('focusable elements', focusableElements);

  /** @type {Promise<DOMRectReadOnly>[]} */
  const elementsRect = focusableElements.map(getElementRect);
  let calculationBase = {
    ...startPositionFallback,
  };

  return Promise.all(elementsRect)
    .then((rectList) => {
      /** @type {Map<HTMLElement, DOMRectReadOnly>} */
      const mapRect = new Map();

      rectList.forEach((rect, index) => {
        if (focusableElements[index] === startElement) {
          calculationBase = rect;
          console.log(
            're-define base on start element rect',
            startElement,
            rect,
          );
        } else {
          mapRect.set(focusableElements[index], rect);
        }
      });

      console.log('Candidates', mapRect);

      const getDistanceRate = calcDistanceRate.bind(
        null,
        direction,
        calculationBase,
      );
      const directionFilter = directionFilters.bind(
        null,
        direction,
        calculationBase,
      );

      const candidates = focusableElements
        .filter((candidate) => {
          const isStart = candidate === startElement;
          isStart && console.log('exclude start element', candidate);
          return !isStart;
        })
        .filter((candidate) => {
          const isMatchDirection = directionFilter(mapRect.get(candidate));
          !isMatchDirection &&
            console.log(
              candidate,
              'excluded due to not bound to direction sector',
            );
          return isMatchDirection;
        })
        .map((candidate) => {
          const candidateRect = mapRect.get(candidate);
          const [distance, deviation] = getDistanceRate(candidateRect);
          console.log(
            candidate,
            `Calculation results distance: ${distance}; deviation ${deviation};`,
          );
          return [candidate, distance, deviation];
        })
        /*
         filter out candidates too deviated (above 80 deg.) from navigation direction.
        */
        .filter(([candidate, , deviation]) => {
          const deviationThreshold = 0.83;
          const isOverDeviationThreshold = deviation < deviationThreshold;
          !isOverDeviationThreshold &&
            console.log(
              candidate,
              `excluded due to deviation ${deviation} too large`,
            );
          return isOverDeviationThreshold;
        });

      const [nearest = null] = candidates
        .sort(byDistanceWithDeviationPenalty)
        .map(([candidate]) => candidate);

      return nearest;
    })
    .catch((error) => {
      console.error('Spatial navigation error', error);
    });
}
